import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import Tooltip from '@src/components/Tooltip';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import { delimitNumber } from '@src/utils/number';
import pluralize from '@src/utils/pluralize';
import useUserInfo from '@src/hooks/useTtsUser';
import { MAX_ROLLOVER_CYCLES } from '@src/constants/tts';

const UsedCredits = ({
  usedCredits,
  totalCredits,
  purchasedTopUpCredits = 0,
  isMobile,
  isAutoRenewalPackage,
  hasNextResetCycle,
  totalCapturedRolloverCredits,
  capturedReferralCreditsCycle = 0,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const { ttsUser } = useUserInfo();
  const hasTopUpCredits = Boolean(purchasedTopUpCredits);
  const hasReferralCredits = Boolean(capturedReferralCreditsCycle);
  const [showInfoCredits, setShowInfoCredits] = useState(false);

  const isTopUpPackage = Boolean(purchasedTopUpCredits);
  const hasCaptureRolloverCredits = Boolean(totalCapturedRolloverCredits);

  const isLockedCycle = ttsUser?.studio?.cycle?.isLocked;
  const isLockedTopUp = ttsUser?.studio?.topUp?.isLocked;

  const maxRolloverCredits = (
    (ttsUser?.studio?.cycle?.creditsPerCycle || 0) * MAX_ROLLOVER_CYCLES
  ).toLocaleString(language);

  const maxRolloverCreditsNote =
    hasNextResetCycle || isAutoRenewalPackage
      ? t('maxRolloverCreditsNote', {
          maxRolloverCredits,
        })
      : '';

  const getTextTopupReferralAndRollover = () => {
    if (!hasReferralCredits && !hasTopUpCredits && !hasCaptureRolloverCredits)
      return '';

    const listNoteCredits = [];

    if (hasReferralCredits) {
      listNoteCredits.push(
        t('rewardCharacters', {
          capturedReferralCreditsCycle: delimitNumber(
            capturedReferralCreditsCycle,
            language,
          ),
        }),
      );
    }

    if (hasTopUpCredits) {
      listNoteCredits.push(
        t('additionalPurchasedCharacters', {
          purchasedTopUpCredits: delimitNumber(purchasedTopUpCredits, language),
        }),
      );
    }

    if (hasCaptureRolloverCredits) {
      listNoteCredits.push(
        t('rolloverCharacters', {
          rolloverCredits: delimitNumber(
            totalCapturedRolloverCredits,
            language,
          ),
        }),
      );
    }

    const note = listNoteCredits.join('; ');
    return `${t('includes')} ${note}.`;
  };

  const handleClickShowInfoTopupAndRollover = () => {
    if (isMobile) setShowInfoCredits(true);
    return null;
  };

  const renderButtonGotIt = () => (
    <div className="used-credits-button-container">
      <Button
        className="button-got-it"
        variant="contained"
        onClick={() => setShowInfoCredits(false)}
      >
        {t('gotItV2')}
      </Button>
    </div>
  );

  const renderNoteLockCredits = () => t('lockCycleCreditsTooltipMyPackage');

  const renderContainerLockCredits = () => {
    if (isLockedCycle)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={renderNoteLockCredits()}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    if (isLockedTopUp)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={renderNoteLockCredits()}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    return <></>;
  };

  return (
    <>
      <Box className="used-credits">
        <Typography className="label">{t('usedCredits')}</Typography>
        <Box className="content-wrapper">
          <Typography className="content">
            {`${delimitNumber(usedCredits, language)}/${delimitNumber(
              totalCredits,
              language,
            )} ${
              isMobile ? '' : pluralize(totalCredits, t('credit'), language)
            }`}
          </Typography>

          {(hasNextResetCycle ||
            isTopUpPackage ||
            isAutoRenewalPackage ||
            hasCaptureRolloverCredits) && (
            <Box className="tooltip">
              <Tooltip
                arrow
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {getTextTopupReferralAndRollover() && (
                      <Typography sx={{ fontSize: '14px' }}>
                        {getTextTopupReferralAndRollover()}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '14px' }}>
                      {maxRolloverCreditsNote}
                    </Typography>
                  </Box>
                }
                placement="left"
                componentsProps={{
                  tooltip: {
                    sx: {
                      padding: '12px',
                    },
                  },
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        textAlign: 'left !important',
                        marginLeft: '16px',
                      },
                    },
                  },
                }}
              >
                <IconButton onClick={handleClickShowInfoTopupAndRollover}>
                  <InfoIcon className="icon" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {!isMobile && renderContainerLockCredits()}
        </Box>
      </Box>
      <NotificationDialogV2
        showIcon={false}
        title={t('detailInfo')}
        description={t('creditNoteOnMobileWeb', {
          addOnCreditsNote: getTextTopupReferralAndRollover(),
          maxRolloverCreditsNote,
        })}
        open={showInfoCredits}
        onClose={() => setShowInfoCredits(false)}
        actionComponent={renderButtonGotIt()}
      />
    </>
  );
};

export default UsedCredits;
